import React, { useCallback, useEffect, useMemo, useState } from "react";
import CardLayout from "components/foundation/templates/CardLayout";
import SelectButton from "components/foundation/SelectButton";
import { CheckIcon } from "assets/icons";
import Button from "components/foundation/Button";
import Divider from "components/foundation/Divider";
import styled from "styled-components";
import { useRecoilState, useRecoilValue, useResetRecoilState } from "recoil";
import {
  DocumentCategoryWithTagsResponse,
  DocumentDuplicateAttachmentDto,
  NoneOrderTagDetailResponse,
} from "apis/data-contracts";
import {
  DOCUMENT_ALLOWED_EXTENSIONS,
  DOCUMENT_UUID,
  KOREA_REGION,
} from "config/constant";
import {
  selectedArchivesBusinessTagsState,
  selectedArchivesCityState,
  selectedArchivesDocumentClassState,
  selectedArchivesProvinceState,
  selectedArchivesTopicTagsState,
} from "store/document/archivesDocument";
import Select from "components/foundation/Select/Select";
import useTagsByDocumentCategoryQuery from "features/tag/hooks/queries/useTagsByDocumentCategoryQuery";
import { useDocumentClassesByCategoryQuery } from "features/document/hooks/queries/useDocumentClassesByCategoryQuery";
import Toast, { useToast } from "components/foundation/Toast";
import { ROUTER_PATH } from "config/path";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import api from "apis";
import Modal from "components/foundation/Modal/Modal";
import ArchivesDuplicateModal from "components/document/Create/CreateDocumentForm/docClass/archives/ArchivesDuplicateModal";
import useApproveRequest from "features/document/hooks/create/useApproveRequest";
import {
  ATTACHMENT_FILES,
  DECISION_MANAGER_FIELD_NAME,
  REFERENCE_MEMBER_FIELD_NAME,
} from "config/documentConstant";
import AttachmentField from "components/document/Create/CreateDocumentForm/CreateDocumentFormField/AttachmentField";
import { DocumentForm } from "features/document/utils/getTempSaveRequest";
import useHomeButton from "features/document/hooks/create/useHomeButton";
import BeforeUnloadModal from "components/Common/BeforeUnloadModal";
import TempDocumentModal from "components/document/Create/TempDocumentModal";
import useTempDocModal from "features/document/hooks/create/useTempDocModal";
import useMediaView from "features/common/hooks/useMediaView";
import {
  TempDocumentDetailResponseWithAny,
  useTempDocumentDetailQuery,
} from "features/document/hooks/queries/useTempDocumentDetailQuery";
import useSaveTempDoc from "features/document/hooks/create/useSaveTempDoc";
import useUpdateTempDoc from "features/document/hooks/create/useUpdateTempDoc";
import FreeTagEditor from "components/document/Create/CreateDocumentForm/CreateDocumentFormHeader/FreeTagEditor";
import { documentFreeTagsState } from "store/document/documentCommon";

const DocumentArchivesCreatePage = () => {
  const tempDocumentUuid = useParams().uuid;

  const { data: tempDocumentDetail } =
    useTempDocumentDetailQuery(tempDocumentUuid);

  /* 태그 */
  const { data: archivesTags } = useTagsByDocumentCategoryQuery(
    DOCUMENT_UUID.ARCHIVES.uuid,
  );

  // 문서 클래스 태그
  const { documentClasses, selectedDocumentClass, setSelectedDocumentClass } =
    useDocumentClassTags();

  const freeTags = useRecoilValue(documentFreeTagsState);

  // 지역 & 도시 태그
  const {
    selectedProvince,
    setSelectedProvince,
    selectedCity,
    setSelectedCity,
    provinceList,
    cityList,
  } = useProvinceAndCityTags({ tempDocumentDetail });

  // 사업구분 태그
  const {
    businessTags,
    selectedBusinessTags,
    setSelectedBusinessTags,
    onClickBusinessTag,
  } = useBusinessTags({ archivesTags });

  // 주제분류 태그
  const { topicTags, selectedTopics, setSelectedTopics, onClickTopicCheckbox } =
    useTopicTags({
      archivesTags,
    });

  /* 폼 */
  const { handleSubmit, setValue, reset, watch, formState } =
    useForm<DocumentForm>({ mode: "onBlur" });

  const { isValid, isSubmitSuccessful } = formState;

  useEffect(() => {
    if (tempDocumentDetail) {
      setSelectedDocumentClass({
        uuid: tempDocumentDetail.documentClassUuid,
        name: tempDocumentDetail.documentClassName,
      });

      const businessTags = tempDocumentDetail.tags.filter(
        (tag) => tag.categoryName === "사업구분",
      );
      if (businessTags.length > 0) {
        setSelectedBusinessTags(
          businessTags.map((tag) => ({
            tagUuid: tag.tagUuid,
            tagName: tag.tagName,
          })),
        );
      }

      const topicTags = tempDocumentDetail.tags.filter(
        (tag) => tag.categoryName === "주제분류",
      );
      if (topicTags.length > 0) {
        setSelectedTopics(
          topicTags.map((tag) => ({
            tagUuid: tag.tagUuid,
            tagName: tag.tagName,
          })),
        );
      }

      reset({
        title: tempDocumentDetail?.name,
        attachmentFiles: tempDocumentDetail?.attachments.map((attachment) => ({
          fileName: attachment.fileName,
          fileHash: attachment.md5Hash,
          documentFileUrl: attachment.fileUrl,
        })),
      });
    }
  }, [tempDocumentDetail]);

  /* BeforeUnload 비활성화 */
  const [beforeUnloadDisable, setBeforeUnloadDisable] = useState(false);

  const getTagUuids = () => {
    const tagUuids = [
      ...selectedBusinessTags.map((tag) => tag.tagUuid),
      ...selectedTopics.map((tag) => tag.tagUuid),
    ];

    return tagUuids;
  };

  /* 임시 저장 */
  // 임시 저장 토스트
  const {
    open: openTempSaveToast,
    onOpenToast: onOpenTempSaveToast,
    onOpenChange: onOpenChangeTempSaveToast,
  } = useToast();

  const { saveTempDoc } = useSaveTempDoc();
  const { updateTempDoc } = useUpdateTempDoc();

  /**
   * @description 임시 저장 버튼 클릭 핸들러
   */
  const onTempSaveDoc = async () => {
    const form = watch();

    const file = form[ATTACHMENT_FILES]?.[0];

    if (!tempDocumentDetail) {
      saveTempDoc({
        saveRequest: {
          name: file?.fileName || selectedDocumentClass?.name,
          documentClassUuid: selectedDocumentClass?.uuid,
          documentData: {},
          tagUuids: getTagUuids(),
          freeTagUuids: freeTags.map((tag) => tag.uuid),
          documentAttachmentFileList: form[ATTACHMENT_FILES],
          provinceName: selectedProvince,
          cityName: selectedCity || undefined,
        },
        successCallback: () => {
          onOpenTempSaveToast(() => {
            setBeforeUnloadDisable(true);
            navigate(ROUTER_PATH.home);
          });
        },
      });
    } else {
      updateTempDoc({
        documentUuid: tempDocumentDetail.uuid,
        updateRequest: {
          name: file?.fileName || selectedDocumentClass?.name,
          documentClassUuid: selectedDocumentClass?.uuid,
          provinceName: selectedProvince,
          cityName: selectedCity || undefined,
          documentData: {},
          documentFileUrl: file?.documentFileUrl,
          tagUuids: getTagUuids(),
          freeTagUuids: freeTags.map((tag) => tag.uuid),
          documentAttachmentFileList: form[ATTACHMENT_FILES],
        },
        successCallback: () => {
          onOpenTempSaveToast(() => {
            setBeforeUnloadDisable(true);
            navigate(ROUTER_PATH.home);
          });
        },
      });
    }
  };

  /* 문서 등록 */
  const { saveAndApproveRequest, approveRequest } = useApproveRequest();

  const navigate = useNavigate();

  // Archives 등록 모달
  const [openDuplicateModal, setOpenDuplicateModal] = useState(false);

  const {
    open: openSaveDocToast,
    onOpenToast: onOpenSaveDocToast,
    onOpenChange: onOpenChangeSaveDocToast,
  } = useToast();

  /**
   * @description 문서 등록 함수
   */
  const registerDocument = async ({
    form,
  }: {
    form: DocumentForm;
    isOverwrite?: boolean;
    md5Hash: string;
  }) => {
    const file = form[ATTACHMENT_FILES][0];

    const approverUuid = form[DECISION_MANAGER_FIELD_NAME];
    const referenceUserUuid = form[REFERENCE_MEMBER_FIELD_NAME];

    // 태그 처리
    if (!tempDocumentDetail) {
      saveAndApproveRequest({
        saveTempDocProps: {
          saveRequest: {
            name: file.fileName!,
            documentClassUuid: selectedDocumentClass?.uuid,
            documentData: {},
            provinceName: selectedProvince,
            cityName: selectedCity || undefined,
            tagUuids: getTagUuids(),
            freeTagUuids: freeTags.map((tag) => tag.uuid),
            documentFileUrl: file.documentFileUrl,
            documentAttachmentFileList: form[ATTACHMENT_FILES],
          },
        },

        approvalRequestProps: {
          approverUuids: approverUuid ? [approverUuid] : undefined,
          referenceUserUuids: referenceUserUuid ? referenceUserUuid : undefined,

          successCallback() {
            onOpenSaveDocToast(() => {
              setBeforeUnloadDisable(true);
              navigate(ROUTER_PATH.document.archive.dashboard);
            });
          },
        },
      });
    } else {
      updateTempDoc({
        documentUuid: tempDocumentDetail.uuid,
        updateRequest: {
          name: file.fileName || selectedDocumentClass?.name,
          documentClassUuid: selectedDocumentClass?.uuid,
          provinceName: selectedProvince,
          cityName: selectedCity || undefined,
          documentData: {},
          documentFileUrl: file?.documentFileUrl,
          tagUuids: getTagUuids(),
          freeTagUuids: freeTags.map((tag) => tag.uuid),
          documentAttachmentFileList: form.attachmentFiles,
        },
        successCallback: () => {
          approveRequest(tempDocumentDetail.uuid, {
            approverUuids: approverUuid ? [approverUuid] : undefined,
            referenceUserUuids: referenceUserUuid
              ? referenceUserUuid
              : undefined,

            successCallback() {
              onOpenSaveDocToast(() => {
                setBeforeUnloadDisable(true);
                navigate(ROUTER_PATH.document.archive.dashboard);
              });
            },
          });
        },
      });
    }
  };

  const [duplicateDocumentList, setDuplicateDocumentList] = useState<
    DocumentDuplicateAttachmentDto[]
  >([]);

  /* Home 버튼 */
  const { openHomeModal, setOpenHomeModal, onClickHomeButton, goToHome } =
    useHomeButton({ setBeforeUnloadDisable });

  // 첨부파일 확인 모달
  const [openFileUploadModal, setOpenFileUploadModal] = useState(false);

  /**
   * @description 등록 버튼 클릭 이벤트 리스너
   */
  const onClickRegisterButton = handleSubmit(async (form: DocumentForm) => {
    // 첨부파일 여부 확인
    if (!form[ATTACHMENT_FILES] || form[ATTACHMENT_FILES].length === 0) {
      setOpenFileUploadModal(true);
      return;
    }

    const file = form[ATTACHMENT_FILES][0];
    const md5Hash = file.fileHash!;

    const checkDuplicateFileRes = await api.document.checkDuplicateFile({
      categoryUuid: DOCUMENT_UUID.ARCHIVES.uuid,
      fileName: file.fileName,
      md5Hash,
    });

    const { duplicate, duplicateDocumentList } =
      checkDuplicateFileRes.data.result;

    if (duplicate) {
      setDuplicateDocumentList(duplicateDocumentList);
      setOpenDuplicateModal(true);
      return;
    }

    registerDocument({ form, md5Hash });
  });

  /* 임시 저장 문서 모달 */
  const isSmallView = useMediaView("small");

  const { openTempSaveDocsModal, setOpenTempSaveDocsModal } = useTempDocModal({
    categoryUuid: DOCUMENT_UUID.ARCHIVES.uuid,
  });

  /* 초기화 */
  useResetArchiveRecoilStates();

  return (
    <>
      <CardLayout.Container
        header={{
          title: "문서 등록",
          description: "문서에 들어갈 기본 태그를 선택해 주세요.",
        }}
        gap={40}
      >
        <DocumentWorkCreateForm
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <CardLayout.Field label="자유태그">
            <FreeTagEditor isTitle={false} />
          </CardLayout.Field>
          <CardLayout.Field label="문서구분">
            <SelectContainerStyle>
              {documentClasses?.map((documentClass) => {
                const selected =
                  documentClass.uuid === selectedDocumentClass?.uuid;

                return (
                  <SelectButton
                    key={documentClass.uuid}
                    type="button"
                    selected={selected}
                    rightIcon={selected && <CheckIcon />}
                    onClick={() => setSelectedDocumentClass(documentClass)}
                  >
                    {documentClass.name}
                  </SelectButton>
                );
              })}
            </SelectContainerStyle>
          </CardLayout.Field>

          <CardLayout.Field label="지역선택">
            <RegionSelectContainerStyle>
              <Select
                list={provinceList}
                placeholder="시/도"
                value={selectedProvince}
                onValueChange={(value: keyof typeof KOREA_REGION) => {
                  setSelectedProvince(value);

                  if (selectedProvince !== value) {
                    // Radix-UI GitHub : Select를 placeholder로 되돌리기
                    // https://github.com/radix-ui/primitives/issues/1569#issuecomment-1196416512
                    setSelectedCity("");
                  }
                }}
              />
              <Select
                list={cityList}
                placeholder="시/군/구"
                value={selectedCity}
                onValueChange={(value) => {
                  if (selectedProvince) {
                    setSelectedCity(value || "");
                  }
                }}
              />
            </RegionSelectContainerStyle>
          </CardLayout.Field>

          <CardLayout.Field label="사업구분">
            <SelectContainerStyle>
              {businessTags?.map((tag) => {
                const selected = !!selectedBusinessTags?.find(
                  (selectedTag) => selectedTag.tagUuid === tag.tagUuid,
                );

                return (
                  <SelectButton
                    key={tag.tagUuid}
                    type="button"
                    selected={selected}
                    rightIcon={selected && <CheckIcon />}
                    onClick={() => onClickBusinessTag(tag)}
                  >
                    {tag.tagName}
                  </SelectButton>
                );
              })}
            </SelectContainerStyle>
          </CardLayout.Field>

          <CardLayout.Field label="주제분류">
            <TopicSelctionContainerStyle>
              {topicTags?.map((topic) => {
                const selected = !!selectedTopics?.find(
                  (selectedTag) => selectedTag.tagUuid === topic.tagUuid,
                );

                return (
                  <SelectButton
                    key={topic.tagUuid}
                    type="button"
                    selected={selected}
                    rightIcon={selected && <CheckIcon />}
                    onClick={() => onClickTopicCheckbox(topic)}
                  >
                    {topic.tagName}
                  </SelectButton>
                );
              })}
            </TopicSelctionContainerStyle>
          </CardLayout.Field>

          <AttachmentField
            vertical
            attachments={watch(ATTACHMENT_FILES)}
            maxAttachments={1}
            uploadParams={{
              fileType: "DOCUMENT_ATTACHMENT",
              isOverwrite: false,
              allowedExtensions: DOCUMENT_ALLOWED_EXTENSIONS,
            }}
            onChangeAttachments={(attachments) => {
              setValue(ATTACHMENT_FILES, attachments);
            }}
          />
        </DocumentWorkCreateForm>

        <Divider />

        <SelectContainerStyle>
          <Button variant="tertiary" size="large" onClick={onClickHomeButton}>
            HOME
          </Button>
          <Button
            variant="primary"
            size="large"
            disabled={!selectedDocumentClass || !isValid}
            onClick={onClickRegisterButton}
          >
            등록
          </Button>
        </SelectContainerStyle>
      </CardLayout.Container>

      <BeforeUnloadModal
        isDirty={formState.isDirty}
        disable={beforeUnloadDisable}
      />

      <Modal.Root open={openHomeModal} onOpenChange={setOpenHomeModal}>
        <Modal.Body
          title="작성한 문서를 임시저장 하시겠습니까?"
          onCancel={goToHome}
          cancelValue="나가기"
          onConfirm={onTempSaveDoc}
          confirmValue="임시저장"
        >
          {"'나가기' 클릭 시 작성한 문서는 저장되지 않습니다."}
        </Modal.Body>
      </Modal.Root>

      <Toast.Message
        open={openTempSaveToast}
        onOpenChange={onOpenChangeTempSaveToast}
      >
        해당 문서가 임시저장 되었습니다.
      </Toast.Message>

      <Toast.Message
        open={openSaveDocToast}
        onOpenChange={onOpenChangeSaveDocToast}
      >
        문서 등록이 완료되었습니다.
      </Toast.Message>

      <Modal.Root
        open={openDuplicateModal}
        onOpenChange={setOpenDuplicateModal}
      >
        <ArchivesDuplicateModal
          duplicateFileList={duplicateDocumentList}
          onConfirm={() => setOpenDuplicateModal(false)}
        />
      </Modal.Root>

      <Modal.Root
        open={
          !isSmallView &&
          !tempDocumentUuid &&
          !openTempSaveToast &&
          !isSubmitSuccessful &&
          openTempSaveDocsModal
        }
        onOpenChange={setOpenTempSaveDocsModal}
      >
        <TempDocumentModal
          defaultTabIndex={2}
          onCancel={() => setOpenTempSaveDocsModal(false)}
        />
      </Modal.Root>

      <Modal.Root
        open={openFileUploadModal}
        onOpenChange={setOpenFileUploadModal}
      >
        <Modal.Body
          title="첨부파일을 기입해주세요."
          onConfirm={() => setOpenFileUploadModal(false)}
          confirmValue="확인"
        >
          {"문서를 등록하려면 첨부파일을 업로드해주세요."}
        </Modal.Body>
      </Modal.Root>
    </>
  );
};

export default DocumentArchivesCreatePage;

/* Hooks */
/**
 * @description 문서구분 태그용 Hook
 */
const useDocumentClassTags = () => {
  /* 문서 구분 태그 */
  const { data: documentClasses } = useDocumentClassesByCategoryQuery(
    DOCUMENT_UUID.ARCHIVES.uuid,
  );

  const [selectedDocumentClass, setSelectedDocumentClass] = useRecoilState(
    selectedArchivesDocumentClassState,
  );

  return {
    documentClasses,
    selectedDocumentClass,
    setSelectedDocumentClass,
  };
};

/**
 * @description 시/도, 시/군/구 태그용 Hook
 */
const useProvinceAndCityTags = ({
  tempDocumentDetail,
}: {
  tempDocumentDetail?: TempDocumentDetailResponseWithAny;
}) => {
  const [selectedProvince, setSelectedProvince] = useRecoilState(
    selectedArchivesProvinceState,
  );
  const [selectedCity, setSelectedCity] = useRecoilState(
    selectedArchivesCityState,
  );

  const provinceList = useMemo(() => {
    return Object.keys(KOREA_REGION).map((region) => ({
      label: region,
      value: region,
    }));
  }, []);

  const cityList = useMemo(() => {
    return selectedProvince
      ? KOREA_REGION[selectedProvince].map((city) => ({
          label: city,
          value: city,
        }))
      : [];
  }, [selectedProvince]);

  useEffect(() => {
    if (!tempDocumentDetail) return;

    setSelectedProvince(
      tempDocumentDetail.provinceName as keyof typeof KOREA_REGION,
    );
    setSelectedCity(tempDocumentDetail?.cityName || "");
  }, [tempDocumentDetail]);

  return {
    selectedProvince,
    setSelectedProvince,
    selectedCity,
    setSelectedCity,
    provinceList,
    cityList,
  };
};

const useBusinessTags = ({
  archivesTags,
}: {
  archivesTags?: DocumentCategoryWithTagsResponse;
}) => {
  /* 사업구분 태그 */
  const businessTags = archivesTags?.tagCategories[0].tags;

  const [selectedBusinessTags, setSelectedBusinessTags] = useRecoilState(
    selectedArchivesBusinessTagsState,
  );

  /**
   * @description 사업구분 태그 선택 이벤트 핸들러
   */
  const onClickBusinessTag = useCallback((tag: NoneOrderTagDetailResponse) => {
    setSelectedBusinessTags((prev) => {
      const newTags = prev.filter((item) => item.tagUuid !== tag.tagUuid);

      if (prev.length !== newTags.length) {
        return newTags;
      } else {
        return [...prev, tag];
      }
    });
  }, []);

  return {
    businessTags,
    selectedBusinessTags,
    setSelectedBusinessTags,
    onClickBusinessTag,
  };
};

const useTopicTags = ({
  archivesTags,
}: {
  archivesTags?: DocumentCategoryWithTagsResponse;
}) => {
  /* 주제분류 태그 */
  const topicTags = archivesTags?.tagCategories[1].tags;

  const [selectedTopics, setSelectedTopics] = useRecoilState(
    selectedArchivesTopicTagsState,
  );

  /**
   * @description 주제 체크박스 클릭 이벤트 핸들러
   */
  const onClickTopicCheckbox = useCallback(
    (tag: NoneOrderTagDetailResponse) => {
      setSelectedTopics((prev) => {
        const newTags = prev.filter((item) => item.tagUuid !== tag.tagUuid);

        if (prev.length !== newTags.length) {
          return newTags;
        } else {
          return [...prev, tag];
        }
      });
    },
    [],
  );

  return {
    topicTags,
    selectedTopics,
    setSelectedTopics,
    onClickTopicCheckbox,
  };
};

/**
 * @description Recoil 초기화용 Hook
 */
const useResetArchiveRecoilStates = () => {
  const resetSelectedArchivesDocumentClass = useResetRecoilState(
    selectedArchivesDocumentClassState,
  );
  const resetSelectedArchivesProvince = useResetRecoilState(
    selectedArchivesProvinceState,
  );
  const resetSelectedArchivesCity = useResetRecoilState(
    selectedArchivesCityState,
  );
  const resetSelectedArchivesBusinessTags = useResetRecoilState(
    selectedArchivesBusinessTagsState,
  );
  const resetSelectedArchivesTopicTags = useResetRecoilState(
    selectedArchivesTopicTagsState,
  );

  useEffect(() => {
    return () => {
      resetSelectedArchivesDocumentClass();
      resetSelectedArchivesProvince();
      resetSelectedArchivesCity();
      resetSelectedArchivesBusinessTags();
      resetSelectedArchivesTopicTags();
    };
  }, []);
};

/* 스타일 */
const DocumentWorkCreateForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 5rem;
`;

const SelectContainerStyle = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(14.2rem, 1fr));
  gap: 1rem;
`;

const RegionSelectContainerStyle = styled.div`
  display: flex;
  gap: 1rem;

  & > * {
    flex: 1;
  }
`;

const TopicSelctionContainerStyle = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(12rem, 1fr));
  gap: 1rem;
`;
